import { InsurancePayer } from "@/services";
import { getBaseRole } from "@/utils/auth-storage";
import {
  PAYER_ADD_ROLES,
  PAYER_ARCHIVE_ROLES,
  PAYER_INITIAL_VALUES,
  PAYER_STATUS_POPUP,
  PAYER_STATUS,
  PayerFormValues,
  PayerPermissions,
  PayerStatusPrompt,
  PayerType,
  PayerUsage,
} from "./payers-constant";

const EMPTY_ADDRESS = PAYER_INITIAL_VALUES.address;

export const toPayerFormValues = (payer: InsurancePayer): PayerFormValues => ({
  payerId: payer.payerId ?? "",
  payerName: payer.payerName ?? "",
  payerType: payer.payerType ?? "",
  status: payer.active === false ? PAYER_STATUS.INACTIVE : PAYER_STATUS.ACTIVE,
  address: {
    line1: payer.address?.line1 ?? EMPTY_ADDRESS.line1,
    line2: payer.address?.line2 ?? EMPTY_ADDRESS.line2,
    city: payer.address?.city ?? EMPTY_ADDRESS.city,
    state: payer.address?.state ?? EMPTY_ADDRESS.state,
    country: payer.address?.country ?? EMPTY_ADDRESS.country,
    zipcode: payer.address?.zipcode ?? EMPTY_ADDRESS.zipcode,
  },
});

export const toPayerPayload = (values: PayerFormValues): InsurancePayer => ({
  payerId: values.payerId.trim(),
  payerName: values.payerName.trim(),
  payerType: values.payerType as PayerType,
  active: values.status === PAYER_STATUS.ACTIVE,
  address: {
    ...values.address,
    line1: values.address.line1.trim(),
    line2: values.address.line2?.trim() || undefined,
    city: values.address.city.trim(),
  },
});

export const getPayerPermissions = (): PayerPermissions => {
  const role = getBaseRole() ?? "";
  const canManage = PAYER_ADD_ROLES.includes(role);
  return {
    canAdd: canManage,
    canEdit: canManage,
    canChangeStatus: canManage,
    canArchive: PAYER_ARCHIVE_ROLES.includes(role),
  };
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null;

export const toPayerUsage = (data: unknown): PayerUsage => {
  const source = isRecord(data) ? data : {};
  return {
    patientCount: Number(source.patientCount) || 0,
    openClaimCount: Number(source.openClaimCount) || 0,
  };
};

export const getPayerStatusCopy = ({ payer, usage }: PayerStatusPrompt) => {
  if (!payer.active) {
    return {
      title: PAYER_STATUS_POPUP.ACTIVATE_TITLE,
      content: `${payer.payerName} ${PAYER_STATUS_POPUP.ACTIVATE}`,
      confirmText: PAYER_STATUS_POPUP.ACTIVATE_CONFIRM,
    };
  }
  const isInUse =
    !!usage && (usage.patientCount > 0 || usage.openClaimCount > 0);
  return {
    title: PAYER_STATUS_POPUP.INACTIVE_TITLE,
    content: isInUse
      ? `${payer.payerName} is linked to ${usage.patientCount} patients and ${usage.openClaimCount} open claims. ${PAYER_STATUS_POPUP.IN_USE_NOTE}`
      : `${payer.payerName} ${PAYER_STATUS_POPUP.NOT_IN_USE}`,
    confirmText: PAYER_STATUS_POPUP.INACTIVE_CONFIRM,
  };
};
